import { useRef } from 'react';
import { ACCEPT_ATTR, ATTACHMENT_CATEGORIES, ATTACHMENT_PURPOSE_ITEMS, ATTACHMENTS_PURPOSE_NOTE, ATTACHMENTS_SECTION_NOTE, FORMATS_HINT, humanSize, validateFile } from './fileRules.ts';
import { stage, missingDescription, type StagedFile } from './uploadQueue.ts';

const STATE_AR: Record<string, string> = {
  pending: 'بانتظار الرفع', uploading: 'جارٍ الرفع…', done: 'تم الرفع', failed: 'فشل الرفع',
};
const STATE_TONE: Record<string, string> = {
  pending: 'text-ink-500', uploading: 'text-brand-700', done: 'text-emerald-700', failed: 'text-rose-700',
};

/**
 * اختيار مرفقات الحجز. لا يستخدم مكوّن الحقول القياسي عمدًا حتى لا يدخل ضمن
 * حقول الحجز التجارية الـ22 ولا يغيّر ترتيبها المعتمد.
 */
export function AttachmentPicker({ files, onChange, disabled, title = 'مرفقات الحجز', busy, showCategory = true, note = null, multiple = true, inputLabel = 'اختيار ملفات مرفقات الحجز', purposeNote = ATTACHMENTS_PURPOSE_NOTE, required = false }:
  { files: StagedFile[]; onChange: (f: StagedFile[]) => void; disabled?: boolean; title?: string; busy?: boolean;
    showCategory?: boolean; note?: string | null; multiple?: boolean; inputLabel?: string; purposeNote?: string | null;
    /** المرفقات إجبارية في هذه الشاشة: نجمةٌ على العنوان و`note` بلون التنبيه (قرار المالك). */
    required?: boolean }) {
  const input = useRef<HTMLInputElement>(null);

  function add(list: FileList | null) {
    if (!list?.length) return;
    const next = [...files];
    for (const file of Array.from(list)) {
      const s = stage(file);
      const err = validateFile(file);
      next.push(err ? { ...s, state: 'failed', error: err } : s);
    }
    onChange(next);
    if (input.current) input.current.value = '';
  }
  const patch = (key: string, part: Partial<StagedFile>) =>
    onChange(files.map(f => (f.key === key ? { ...f, ...part } : f)));
  const remove = (key: string) => onChange(files.filter(f => f.key !== key));

  return (
    <section data-attachments className="rounded-lg border border-slate-200 bg-slate-50/60 p-3" aria-label={title}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-ink-900">
          {title}{required && <span aria-hidden="true" className="text-rose-600"> *</span>}
        </h3>
        <button type="button" className="btn-ghost px-3 py-1 text-sm" disabled={disabled || busy}
          onClick={() => input.current?.click()}>
          إضافة ملفات
        </button>
      </div>
      {purposeNote && (
        <>
          <p className="mt-1 text-xs text-ink-700">{ATTACHMENTS_SECTION_NOTE}</p>
          {/**
            * المستندات المطلوبة **شاراتٌ ملوّنة** لا سطرًا رماديًّا (قرار المالك: «اعمل
            * دول بلون ووضّحهم علشان ياخد باله منهم»). سطرٌ واحد بخطٍّ صغير تمرّ عليه
            * العين ولا تقرؤه؛ وخمس شاراتٍ تُعدّ بالنظر فيُعرف الناقص منها.
            */}
          {purposeNote === ATTACHMENTS_PURPOSE_NOTE ? (
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <span className="text-xs font-semibold text-ink-700">المطلوب عادةً:</span>
              {ATTACHMENT_PURPOSE_ITEMS.map(it => (
                <span key={it}
                  className="rounded-full border border-brand-200 bg-brand-50 px-2 py-0.5 text-xs font-semibold text-brand-800">
                  {it}
                </span>
              ))}
            </div>
          ) : (
            <p className="mt-1 text-xs text-ink-500">{purposeNote}</p>
          )}
        </>
      )}
      <p className="mt-1 text-xs text-ink-500">{FORMATS_HINT}</p>
      {/* حين تكون إجبارية، الجملة تُقرأ قبل الضغط لا بعد الرفض */}
      {note && <p className={required
        ? 'mt-1 rounded-lg bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-900'
        : 'mt-1 text-xs text-ink-500'}>{note}</p>}
      <input ref={input} type="file" multiple={multiple} accept={ACCEPT_ATTR} className="sr-only"
        aria-label={inputLabel} disabled={disabled || busy}
        onChange={e => add(e.target.files)} />

      {files.length > 0 && (
        <ul className="mt-3 space-y-2">
          {files.map(f => (
            <li key={f.key} className="rounded-lg border border-slate-200 bg-white p-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="min-w-0 flex-1 truncate text-sm" title={f.file.name}>{f.file.name}</span>
                <span className="text-xs tabular-nums text-ink-500">{humanSize(f.file.size)}</span>
                <span className={`text-xs ${STATE_TONE[f.state]}`}>{STATE_AR[f.state]}</span>
                {f.state !== 'done' && !busy && (
                  <button type="button" className="btn-ghost px-2 py-0.5 text-xs" onClick={() => remove(f.key)}>إزالة</button>
                )}
              </div>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {showCategory && (
                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-ink-700">نوع المرفق</span>
                    <select className="control" value={f.category} disabled={disabled || busy || f.state === 'done'}
                      onChange={e => patch(f.key, { category: e.target.value })}>
                      {ATTACHMENT_CATEGORIES.filter(c => c.code !== 'ACCOUNT_SETTLEMENT').map(c => <option key={c.code} value={c.code}>{c.ar}</option>)}
                    </select>
                  </label>
                )}
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-ink-700">
                    وصف مختصر{showCategory && f.category === 'OTHER' ? ' (مطلوب)' : ''}
                  </span>
                  <input className="control" value={f.description} disabled={disabled || busy || f.state === 'done'}
                    placeholder="مثال: محادثة واتساب بتأكيد الموعد"
                    onChange={e => patch(f.key, { description: e.target.value })} />
                </label>
              </div>
              {showCategory && missingDescription(f) && f.state !== 'done' && (
                <p role="alert" className="mt-1 text-xs text-rose-600">الوصف مطلوب عند اختيار «مستند آخر متعلق بالحجز»</p>
              )}
              {f.error && <p role="alert" className="mt-1 text-xs text-rose-600">{f.error}</p>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
